import React, { useEffect, useRef, useState } from "react";
import { Spin } from "antd";
import { fetchUnitHistory } from "../../api/OrderItemUnits";
import { renderAlertMessage } from "../../helpers/common";

// Expandable row rendered by UnitRow directly below the unit whose kebab
// "View scan history" was clicked (mockup .scan-log-row/.scan-log-panel).
// Only ever mounted while that toggle is on, so the fetch effectively runs
// "on open" per the plan, and the fetched data is dropped for free when the
// row is collapsed again (re-fetches fresh on the next open).
function sortNewestFirst(list) {
  return (list || [])
    .slice()
    .sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
}

const EM_DASH = "—";

// Mirrors the mockup's formatLogDate (native Intl, not moment) so a status
// log's "Jul 31, 2026, 12:24 PM" reads identically here and in the mockup —
// moment's format string can't produce the comma before the time without
// awkward escaping, so this sticks to toLocaleString like the source design.
function formatLogDate(at) {
  if (!at) return null;
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Kept local (rather than importing UnitRow's statusPillClass) to avoid a
// circular import: UnitRow already imports this file to render the panel.
// Same status -> pill-color mapping as UnitRow/ItemCard.
function statusPillClass(status) {
  if (status === "Exception") return "exception";
  if (!status || status === "Pending") return "";
  return "progress";
}

function capitalize(value) {
  if (!value) return null;
  const str = String(value);
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function measurementKindLabel(kind) {
  if (kind === "reweigh") return "Re-Weighed";
  if (kind === "booked") return "Booked";
  return capitalize(kind);
}

// Always 2dp, e.g. "60.00 lb" — a raw DB value like 60 or "60.5" both read
// as deliberately-measured weights, so trailing zeros are shown rather than
// dropped the way a plain number cell would clumsily mix "60" and "60.50".
function formatWeight(measurement) {
  const raw = measurement.weight;
  if (raw === null || raw === undefined || raw === "") return null;
  const num = Number(raw);
  const formatted = Number.isFinite(num) ? num.toFixed(2) : String(raw);
  const uom = measurement.weight_uom || "";
  return `${formatted}${uom ? ` ${uom}` : ""}`;
}

// Whole numbers render plain ("10"), anything with a fractional part rounds
// to 2dp ("10.5" -> "10.50") — consistent without forcing ".00" onto every
// integer dimension the way the weight column intentionally does.
function formatDimValue(value) {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

function formatDims(measurement) {
  const { length, width, height, dim_uom: dimUom } = measurement;
  const values = [length, width, height].map(formatDimValue);
  if (values.every((v) => v === null)) return null;
  const filled = values.map((v) => (v === null ? "0" : v));
  const uom = dimUom || "";
  return `${filled.join(" × ")}${uom ? ` ${uom}` : ""}`;
}

const UnitHistoryPanel = ({ unitId, unitLabel, colSpan }) => {
  const [loading, setLoading] = useState(true);
  const [statusLogs, setStatusLogs] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!unitId) return undefined;
    setLoading(true);
    fetchUnitHistory(unitId)
      .then((result) => {
        if (!isMounted.current) return;
        if (result.success) {
          setStatusLogs(sortNewestFirst(result.status_logs));
          setMeasurements(sortNewestFirst(result.measurements));
        } else {
          renderAlertMessage(result.errors);
        }
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
    return undefined;
  }, [unitId]);

  return (
    <tr className="scan-log-row">
      <td />
      <td colSpan={colSpan}>
        <div className="scan-log-panel">
          {loading ? (
            <div className="scan-log-loading">
              <Spin size="small" />
            </div>
          ) : (
            <React.Fragment>
              <div className="scan-log-section">
                <div className="scan-log-title">Status history &mdash; {unitLabel}</div>
                <table className="scan-log-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Date</th>
                      <th>By</th>
                      <th>Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statusLogs.length === 0 ? (
                      <tr>
                        <td className="scan-log-empty" colSpan={4}>No status changes yet</td>
                      </tr>
                    ) : (
                      statusLogs.map((log, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={`${log.status}-${log.at}-${idx}`}>
                          <td>
                            <span className={`status-pill ${statusPillClass(log.status)}`}>
                              {log.status || EM_DASH}
                            </span>
                          </td>
                          <td className="date">{formatLogDate(log.at) || EM_DASH}</td>
                          <td>{log.by_name || EM_DASH}</td>
                          <td>{log.via || EM_DASH}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="scan-log-section">
                <div className="scan-log-title">Measurements</div>
                <table className="scan-log-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Weight</th>
                      <th>Dimensions</th>
                      <th>Source</th>
                      <th>By</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {measurements.length === 0 ? (
                      <tr>
                        <td className="scan-log-empty" colSpan={6}>No measurements recorded yet</td>
                      </tr>
                    ) : (
                      measurements.map((m, idx) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={`${m.kind}-${m.at}-${idx}`}>
                          <td>{measurementKindLabel(m.kind) || EM_DASH}</td>
                          <td className="num">{formatWeight(m) || EM_DASH}</td>
                          <td className="num">{formatDims(m) || EM_DASH}</td>
                          <td>{capitalize(m.source) || EM_DASH}</td>
                          <td>{m.by_name || EM_DASH}</td>
                          <td className="date">{formatLogDate(m.at) || EM_DASH}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </React.Fragment>
          )}
        </div>
      </td>
    </tr>
  );
};

export default UnitHistoryPanel;
